import React, { useEffect, useRef, useState } from 'react'
import {
  Form,
  FormDataConsumer,
  NumberInput,
  RadioButtonGroupInput,
  TextInput,
  required,
  useNotify,
  useTranslate,
  useRefresh,
} from 'react-admin'
import {
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
  Hidden,
} from '@mui/material'
import { CardCommon } from 'src/components/uiElements'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { blue } from 'src/assets/colors'
import { reservationServices } from 'src/services/sales/reservation/reservation.service'
import { DatePickers, DateTimePickers } from 'src/components/uiElements/react-hook-fields'
import { Add, Delete, Place, Save } from '@mui/icons-material'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { LoadingButton } from '@mui/lab'
import { AutocompleteInput } from 'src/components/uiElements/fields/AutocompleteInput'
import dayjs from 'dayjs'
import { getSupplierByProduct } from 'src/components/pages/sales/reservation/helper/getSupplierByProduct'
import { AirScaleRow } from './AirScaleRow'
import { customAwait } from 'src/components/pages/sales/reservation/helper/customAwait'
import { useCardId } from 'src/components/pages/sales/reservation/context/CardIdContext'
import { itemServices } from 'src/services/reservation/item/item.service'
import { destinationsServices } from 'src/services/external/destination.service'
import { productServices } from 'src/services/reservation/product/product.service'
import { businessUnitServices } from 'src/services/reservation/businessUnit/businessUnit.service'
import { PRICING } from 'src/components/pages/sales/reservation/logic/common/pricing'
import { INVOICED, CONFIRMED, CANCELLED } from 'src/components/pages/sales/reservation/logic/common/state'
import { tagServices } from 'src/services/reservation/tag/tag.service'
import { FilesCard } from '../common/FilesCard'
import { PERMISSION_GET } from 'src/helper/Permission'
import { HasPermission } from 'src/helper/HasPermission'

const PASSENGER_FIELDS = ['name', 'document_number', 'nationality', 'country', 'ticket_number']
const NEW_PASSENGER_FIELDS = ['name', 'surname', 'ticket_number']

export const AirForm = ({
  openModal,
  reservation,
  passengers,
  workUnit,
  product,
  handleClose,
  onSubmit,
  recordProduct,
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const filesCardRef = useRef(null)
  const [record, setRecord] = useState({})
  const [sources, setSources] = useState([])
  const [loadingSources, setLoadingSources] = useState(false)
  const [providers, setProviders] = useState([])
  const [currencies, setCurrencies] = useState([])
  const [loadingCurrencies, setLoadingCurrencies] = useState(false)
  const [tags, setTags] = useState([])
  const [loadingTags, setLoadingTags] = useState(false)
  const [timeoutTags, setTimeoutTags] = useState()
  const [destinations, setDestinations] = useState([[[], []]])
  const [loadingDestinations, setLoadingDestinations] = useState([[false, false]])
  const [timeoutDestinations, setTimeoutDestinations] = useState([[null, null]])
  const [aditionalPassengers, setAditionalPassengers] = useState([])
  const [loadingButton, setLoadingButton] = useState(false)
  const [retrieve, setRetrieve] = useState()
  const [loadingRetrieve, setLoadingRetrieve] = useState(false)
  const [availableTickets, setAvailableTickets] = useState([])
  const [isNetviax, setIsNetviax] = useState(false)
  const [passengersAux, setPassengersAux] = useState(passengers ? [...passengers] : [])
  const [loadingSupplier, setLoadingSupplier] = useState(false)

  const { cardId } = useCardId()

  useEffect(() => {
    getSources()
    getTags()
    setRecord({
      number: reservation?.['number'],
      client: reservation?.['business_unit_relation']?.['long_name'],
      segments: [{}],
    })
    const providersAux = reservation?.['product']
      ?.find((elem) => elem.ref_table_detail === 'air_detail')
      ?.['supplier_relation']?.map((elem) => {
        const [key, value] = Object.entries(elem)[0]
        return { id: key, name: value }
      })

    ;(async () => {
      let providers = []
      try {
        setLoadingSupplier(true)
        if (!providersAux || (providersAux && providersAux.length === 0)) {
          providers = await getSupplierByProduct(product)
        }
        setLoadingSupplier(false)
      } catch (error) {
        providers = []
        setLoadingSupplier(false)
      }
      setProviders(providersAux || providers)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (reservation?.business_unit_id) {
      getCurrencies(reservation?.business_unit_id)
    }
  }, [reservation])

  const getDestinationByIata = async (iata) => {
    try {
      const destinations = await getDestinationsService(iata)
      const destinationFind = (
        destinations?.data?.find((elem) => elem.category === 'Aeropuertos')?.data || []
      ).find((destination) => destination.iata === iata)
      return destinationFind
    } catch (error) {
      return null
    }
  }

  useEffect(() => {
    ;(async () => {
      if (recordProduct) {
        const sources = await getSourcesService()
        const sourceFind = sources.find((source) => source.id === recordProduct?.source_id)
        const destinations = await getDestinationsService(recordProduct?.destination)
        const destinationFind = (
          destinations?.data?.find((elem) => elem.category === 'Aeropuertos')?.data || []
        ).find((destination) => destination.label === recordProduct?.destination)
        const cancelInfoRelationFind = recordProduct?.cancel_info_relation.find(
          (cancel) => !cancel?.is_client_cancel
        )
        const segments = recordProduct?.segments?.[0]?.itineraryGroup

        const segmentPromises = segments.map(async (segment) => {
          const origin = await getDestinationByIata(segment?.boardPoint)
          const destination = await getDestinationByIata(segment?.offPoint)

          return {
            ...segment,
            flightNumber: segment?.scales?.[0].flightNumber,
            date_in: dayjs(`${segment?.departureDate}T${segment?.departureTime}`),
            date_out: dayjs(`${segment?.arrivalDate}T${segment?.arrivalTime}`),
            origin: origin,
            destination: destination,
          }
        })
        const resolvedSegments = await Promise.all(segmentPromises)
        setRecord({
          number: reservation?.['number'],
          client: reservation?.['business_unit_relation']?.['long_name'],
          ...recordProduct,
          source: sourceFind,
          destination: destinationFind,
          cancellation_date: '2023-08-15',
          cancellation_amount: cancelInfoRelationFind?.amount,
          net_price: recordProduct?.pricing_relation?.[0]?.net_price,
          pax_titular: recordProduct?.pax_relation?.[0]?.id,
          pax_info: recordProduct?.pax_relation?.[0]?.id,
          segments: resolvedSegments,
        })
      }
    })()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordProduct])

  const getDestinationsService = async (name) => {
    try {
      const response = await destinationsServices.getDestinationFlightByName(name)
      return response
    } catch (error) {
      throw error
    }
  }

  const getSourcesService = async () => {
    try {
      const response = await reservationServices.getSources()
      return response
    } catch (error) {
      throw error
    }
  }

  const getBusinessUnitService = async (businessUnitId) => {
    try {
      const response = await businessUnitServices.getById(businessUnitId)
      return response
    } catch (error) {
      throw error
    }
  }

  const getSources = async () => {
    try {
      setLoadingSources(true)
      const response = await getSourcesService()
      setSources(response)
      setLoadingSources(false)
    } catch (error) {
      setSources([])
      setLoadingSources(false)
    }
  }

  const getCurrencies = async (business_unit_id) => {
    try {
      setLoadingCurrencies(true)
      const response = await getBusinessUnitService(business_unit_id)
      if (response) {
        const allCurrencies = [...response.currency_relation, ...response.currency_inheritance]
        const currenciesAux = []
        const currenciesToAux = []
        allCurrencies?.forEach((currency) => {
          if (!currenciesAux.find((elem) => elem.id === currency.currency_id)) {
            currenciesAux.push(currency.currency_relation)
          }
          if (
            !currenciesToAux.find(
              (elem) =>
                elem.currency_id === currency.currency_id && elem.currency_to_id === currency.currency_to_id
            )
          ) {
            currenciesToAux.push(currency)
          }
        })
        setCurrencies(currenciesAux)
      }
    } catch (error) {
      setCurrencies([])
    } finally {
      setLoadingCurrencies(false)
    }
  }

  const getTags = async () => {
    clearTimeout(timeoutTags)
    try {
      const timeout = setTimeout(async () => {
        setLoadingTags(true)
        const tagsResponse = await tagServices.getTagsByWorkUnit(reservation?.['work_unit_id'])
        setTags(tagsResponse)
        setLoadingTags(false)
      }, 1000)
      setTimeoutTags(timeout)
    } catch (error) {
      setTags([])
      setLoadingTags(false)
    }
  }

  const getDestinations = async (name, index, indexDestination) => {
    clearTimeout(timeoutDestinations[index]?.[indexDestination])
    let loadingDestinationsAux = [...loadingDestinations]
    loadingDestinationsAux[index] = [...(loadingDestinationsAux[index] || [])]
    let destinationsAux = [...destinations]
    destinationsAux[index] = [...(destinationsAux[index] || [])]

    if (!name || name.length < 3) {
      destinationsAux[index][indexDestination] = []
      setDestinations(destinationsAux)
      return // Detener la ejecución si el nombre es menor de 3 caracteres
    }

    try {
      const timeout = setTimeout(async () => {
        loadingDestinationsAux[index][indexDestination] = true
        setLoadingDestinations(loadingDestinationsAux)
        const response = await getDestinationsService(name)
        let filteredDestinations = response?.data?.find((elem) => elem.category === 'Aeropuertos')?.data || []
        filteredDestinations = filteredDestinations.filter((elem) => elem.iata !== null)
        destinationsAux[index][indexDestination] = filteredDestinations
        setDestinations(destinationsAux)
        loadingDestinationsAux[index][indexDestination] = false
        setLoadingDestinations(loadingDestinationsAux)
      }, 1000)
      let timeoutDestinationsAux = [...timeoutDestinations]
      timeoutDestinationsAux[index] = [...(timeoutDestinationsAux[index] || [])]
      timeoutDestinationsAux[index][indexDestination] = timeout
      setTimeoutDestinations(timeoutDestinationsAux)
    } catch (error) {
      destinationsAux[index][indexDestination] = []
      setDestinations(destinationsAux)
      loadingDestinationsAux[index][indexDestination] = false
      setLoadingDestinations(loadingDestinationsAux)
    }
  }

  const onClickManuallyMode = () => {
    setRetrieve(null)
    setPassengersAux([...passengers])
  }

  const getAvailableTickets = async (formData) => {
    try {
      setLoadingRetrieve(true)
      const response = await reservationServices.send_air_ticket(formData.externalId, product.product_id)
      if (response) {
        const isNetviax = Array.isArray(response)
        if (isNetviax) {
          setIsNetviax(true)
          setAvailableTickets(response)
        } else {
          setIsNetviax(false)
          formatInfoTickets(response, formData)
        }
      } else {
        setAvailableTickets([])
        notify('ra.notification.pnr_used', { type: 'error' })
      }
    } catch (error) {
      setAvailableTickets([])
      setRetrieve(null)
      setRecord({ ...formData })
      setIsNetviax(false)
      setPassengersAux([...passengers])
      notify('ra.notification.error_retrieve', { type: 'error' })
    } finally {
      setLoadingRetrieve(false)
    }
  }

  const formatInfoTickets = (response, formData) => {
    try {
      setLoadingRetrieve(true)
      const { record, passengersTickets } = formatResponse(response, formData, currencies, sources)
      setPassengersAux([...passengers, ...passengersTickets])
      setRecord(record)
      setRetrieve(response)
    } catch (error) {
      setAvailableTickets([])
      setRetrieve(null)
      setRecord({ ...formData })
      setPassengersAux([...passengers])
      notify('ra.notification.error_retrieve', { type: 'error' })
    } finally {
      setLoadingRetrieve(false)
    }
  }

  const getRetriveCurrency = (currencies, currency_origin) => {
    try {
      const currency_id = currencies.find((elem) => elem.short_name === currency_origin)?.id
      if (!currency_id) {
        notify('ra.notification.currency_not_found_in_business_unit', {
          type: 'error',
          messageArgs: { currency: currency_origin },
        })
        return ''
      }
      return currency_id
    } catch (error) {
      notify('ra.notification.currency_not_found_in_business_unit', {
        type: 'error',
        messageArgs: { currency: currency_origin },
      })
      return ''
    }
  }

  const formatResponse = (response, formData, currencies, sources) => {
    try {
      response?.segments?.[0]?.itineraryGroup.forEach((elem) => {
        const departureDate =
          elem.departureDate && elem.departureTime
            ? dayjs(`${elem.departureDate} ${elem.departureTime}`)
            : null
        const arrivalDate =
          elem.arrivalDate && elem.arrivalTime ? dayjs(`${elem.arrivalDate} ${elem.arrivalTime}`) : null
        const timeMin = departureDate && arrivalDate ? arrivalDate.diff(departureDate, 'minute') : null
        const hours = timeMin ? Math.floor(timeMin / 60) : null
        const minutes = timeMin ? timeMin - hours * 60 : null
        elem.totalDuration =
          hours !== null && minutes !== null ? `${hours} hora(s) ${minutes} minuto(s)` : null
      })

      const passengersTickets = response?.extra?.extra?.map((elem, i) => {
        const pax = {
          ...elem,
          id: elem.id || `id-${i}`,
          document_number: elem.document_number,
        }

        if (pax.document_number && typeof pax.document_number !== 'string') {
          pax.document_number = `${pax.document_number}`
        }

        return pax
      })
      const fare_amount = response?.extra?.extra?.[0]?.fare_amount || response?.pricing?.fare_amount || 0
      const currency_origin =
        response?.extra?.extra?.[0]?.currency_origin || response?.pricing?.currency_origin

      const record = {
        ...formData,
        pnr_code: response?.pnr_code,
        office_id: response?.office,
        currency_id: getRetriveCurrency(currencies, currency_origin),
        net_price: fare_amount,
        cancellation_amount: fare_amount,
        gds: response?.gds,
        flightNumber: response?.segments[0].itineraryGroup[0].scales[0].flightNumber,
        airline: response?.segments[0].itineraryGroup[0]?.airline,
        origin: response?.origin,
        destination: response?.destination,
        document_number: null,
        status: response?.booking_status || INVOICED,
        pax_titular: passengersTickets?.[0]?.id || formData.pax_titular || '',
        pax_info: passengersTickets?.[0]?.id || formData.pax_info || '',
        cancellation_date: dayjs(),
        source: sources.find((elem) => elem.long_name === 'OFFLINE') || null,
        segments: response?.segments,
      }

      return { record, passengersTickets }
    } catch (error) {
      return {}
    }
  }

  const getInfoTickets = async (formData) => {
    try {
      setLoadingRetrieve(true)
      const documentNumber =
        formData.document_number?.[0] === 'Todos'
          ? availableTickets.map((elem) => elem.document_number)
          : formData.document_number
      const response = await reservationServices.getAirInfoTickets(documentNumber)

      const { record, passengersTickets } = formatResponse(response, formData, currencies, sources)

      setPassengersAux([...passengers, ...passengersTickets])
      setRecord(record)
      setRetrieve(response)
      setLoadingRetrieve(false)
      setAvailableTickets((current) =>
        current.filter((elem) => !documentNumber.includes(elem.document_number))
      )
    } catch (error) {
      setAvailableTickets([])
      setRetrieve(null)
      setLoadingRetrieve(false)
      setRecord({ ...formData })
      setPassengersAux([...passengers])
      notify('ra.notification.error_retrieve', { type: 'error' })
    }
  }

  const addSegment = (formData) => {
    setRecord({
      ...formData,
      segments: [...formData.segments, {}],
    })
  }

  const removeSegment = (formData, index) => {
    const formDataAux = JSON.parse(JSON.stringify(formData))
    formDataAux.segments.splice(index, 1)
    setRecord(formDataAux)
  }

  const onChangeOrigin = (formData, origin, index) => {
    const segments = formData.segments
    segments[index].origin = origin

    setRecord({
      ...formData,
      segments,
    })
  }

  const onChangeOriginType = (formData, type_origin, index) => {
    const segments = formData.segments
    segments[index].type_origin = type_origin

    setRecord({
      ...formData,
      segments,
    })
  }

  const onChangeDestination = (formData, destination, index) => {
    const segments = formData.segments
    segments[index].destination = destination

    setRecord({
      ...formData,
      segments,
    })
  }

  const onChangeDestinationType = (formData, type_destination, index) => {
    const segments = formData.segments
    segments[index].type_destination = type_destination

    setRecord({
      ...formData,
      segments,
    })
  }

  const getAvailablePassengers = (formData, filterAditional = false, aditionalPassengerId = null) => {
    let passengerIds = [formData?.pax_info]

    if (filterAditional) {
      passengerIds = [...passengerIds, ...aditionalPassengers.map((elem) => elem.id && !elem.isNew)]
    }

    if (aditionalPassengerId) {
      passengerIds = passengerIds.filter((elem) => elem !== aditionalPassengerId)
    }

    return passengersAux.filter((passenger) => !passengerIds.includes(passenger.id))
  }

  const onChangeTitularPassenger = (passenger, formData) => {
    const formDataAux = JSON.parse(JSON.stringify(formData))
    formDataAux.pax_info = passenger
    setRecord(formDataAux)

    const aditionalPassengersAux = [...aditionalPassengers]
    const indexPassenger = aditionalPassengersAux.findIndex((elem) => elem.id === passenger)
    if ((!isNaN(passenger) || !passenger.includes('new')) && indexPassenger > -1) {
      aditionalPassengersAux.splice(indexPassenger, 1)
      setAditionalPassengers(aditionalPassengersAux)
    }
  }

  const onChangePassengerField = (value, index, field) => {
    const passengersAux = [...passengers]
    passengersAux[index][field] = value
    setPassengersAux(passengersAux)
  }

  const onChangePassenger = (value, index, field) => {
    const aditionalPassengersAux = [...aditionalPassengers]
    aditionalPassengersAux[index][field] = value
    setAditionalPassengers(aditionalPassengersAux)
  }

  const onChangeAditionalPassenger = (passenger, index) => {
    const aditionalPassengersAux = [...aditionalPassengers]
    aditionalPassengersAux[index] = passengers.find((elem) => elem.id === passenger)
    setAditionalPassengers(aditionalPassengersAux)
  }

  const addPassenger = (isNew = false) => {
    const index =
      parseInt(
        aditionalPassengers
          ?.filter((elem) => elem.isNew)
          ?.map((elem) => elem.id.split('_')[1])
          ?.reverse()?.[0] || -1
      ) + 1
    setAditionalPassengers([
      ...aditionalPassengers,
      {
        isNew,
        id: `new_${index}`,
      },
    ])
  }

  const removePassenger = (index) => {
    const aditionalPassengersAux = [...aditionalPassengers]
    aditionalPassengersAux.splice(index, 1)
    setAditionalPassengers(aditionalPassengersAux)
  }

  const getPassengerInfo = (passengerId, field) => {
    const allPassengers = [
      ...passengersAux,
      ...aditionalPassengers.filter((elem) => elem.isNew && elem.name && elem.surname),
    ]
    const passenger = allPassengers.find((elem) => elem.id === passengerId)
    if (passenger && field === 'name') {
      return `${passenger.name} ${passenger.surname}`
    } else {
      return passenger?.[field] || ''
    }
  }

  const getSegmentInfo = (data) => {
    const dateIn = data.date_in ? dayjs(data.date_in) : null
    const dateOut = data.date_out ? dayjs(data.date_out) : null
    const timeDiff = dateIn && dateOut ? dateOut.diff(dateIn, 'minutes') : null
    const hours = timeDiff ? Math.floor(timeDiff / 60) : null
    const minutes = timeDiff ? timeDiff - hours * 60 : null

    return {
      ...data,
      departureDate: dateIn?.format('YYYY-MM-DD'),
      arrivalDate: dateOut?.format('YYYY-MM-DD'),
      departureTime: data.date_in ? dayjs(data.date_in).format('HH:mm:ss') : null,
      arrivalTime: data.date_out ? dayjs(data.date_out).format('HH:mm:ss') : null,
      flightNumber: data?.scales?.[0]?.flightNumber,
      total_duration: hours !== null && minutes !== null ? `${hours} hora(s) ${minutes} minuto(s)` : null,
    }
  }

  const getPassenger = (passengerId) => {
    const passenger = { ...aditionalPassengers.find((elem) => elem.id === passengerId) }
    if (isNaN(passengerId)) {
      delete passenger.id
      delete passenger.isNew
    }
    return passenger
  }

  const getPassengersToSend = (data, addAditional = true, deleteId = true, addRtrieve = false) => {
    const passenger =
      data.pax_info && !isNaN(data.pax_info) ? [passengersAux.find((elem) => elem.id === data.pax_info)] : []
    const aditionalPassengersAux = addAditional
      ? aditionalPassengers?.map((elem) => getPassenger(elem.id))
      : []
    const retrievePassengers = addRtrieve
      ? retrieve?.extra?.extra
          ?.map((elem, i) => ({ ...elem, id: elem.id || `id-${i}` }))
          ?.filter((elem) => !passenger.find((elemAux) => elem.id === elemAux.id)) || []
      : []

    let passengersToSend = [...passenger, ...aditionalPassengersAux, ...retrievePassengers]
    passengersToSend = JSON.parse(JSON.stringify(passengersToSend))

    if (deleteId) {
      passengersToSend.forEach((elem) => {
        if (typeof elem.id === 'string' && elem.id.includes('id-')) {
          delete elem.id
        }
      })
    }

    return passengersToSend
  }

  const postSave = async (data) => {
    const allPassengers = [
      ...passengersAux,
      ...aditionalPassengers.filter((elem) => elem.isNew && elem.name && elem.surname),
    ]
    let paxTitular = allPassengers.find((elem) => elem.id === data.pax_titular) || {}
    if (
      typeof paxTitular.id === 'string' &&
      (paxTitular.id.includes('id-') || paxTitular.id.includes('new'))
    ) {
      paxTitular = JSON.parse(JSON.stringify(paxTitular))
      delete paxTitular.id
      delete paxTitular.isNew
    }

    let productVar = null
    if (recordProduct) {
      productVar = await productServices.getProductByWorkUnitWithId(product?.product_by_work_unit_id)
    }

    const airline = data?.airline || data?.segments?.[0]?.airline
    let dataToSend = {
      ...data,
      product_id: product?.product_id,
      product_token: product?.token_id || productVar?.token_id,
      ref_table_detail: 'air_detail',
      reservation_id: reservation.id,
      tag_relation: data.tag_relation.map((elem) => ({ id: elem.id })),
      pax_titular: paxTitular,
      cancellation_date: data.cancellation_date ? dayjs(data.cancellation_date).format('YYYY-MM-DD') : null,
      source_id: data.source.id,
      destination_type: Number(data.destination_type),
      airline: airline,
      supplier: airline,
      flightNumber: data?.flightNumber || data?.segments?.[0]?.flightNumber, // must be in main node because(old logic where arline was out of segments)
    }

    let dataToSendEdit = {
      ...data,
      id: data?.item_id,
      product_token: product?.token_id || productVar?.token_id,
      destination_type: Number(data?.destination_type),
      source_id: data.source.id,
      cancellation_date: data.cancellation_date ? dayjs(data.cancellation_date).format('YYYY-MM-DD') : null,
      cancel_info_relation: data?.cancel_info_relation?.map((cancel) => {
        if (!cancel?.is_client_cancel) {
          return {
            ...cancel,
            date: data.cancellation_date ? dayjs(data.cancellation_date).format('YYYY-MM-DD') : null,
            amount: data?.cancellation_amount,
          }
        }
        return cancel
      }),

      detail: {
        code: data.code,
        id: data?.id_detail,
        status: data?.status_detail,
        supplier_id: data.supplier_id,
        source: data.source.label,
        destination: data.destination?.label,
        flightNumber: data?.segments?.[0]?.scales?.[0]?.flightNumber,
        segments: data?.segments,
        gds: data?.gds,
      },
    }

    if (retrieve) {
      delete dataToSend.gds
      delete dataToSend.destination_type
      delete dataToSend.segments
      delete dataToSend.document_number
      delete dataToSend.net_price
      dataToSend = {
        ...dataToSend,
        is_retrieve: true,
        pax_info: getPassengersToSend(data),
        detail: retrieve,
      }
    } else {
      delete dataToSend.externalId
      delete dataToSend.office_id
      delete dataToSend.segments
      dataToSend = {
        ...dataToSend,
        segments: data.segments.map((detail) => getSegmentInfo(detail)),
        pax_info: getPassengersToSend(data),
      }

      dataToSendEdit['detail']['segments'] = data.segments.map((detail) => getSegmentInfo(detail))
    }
    delete dataToSendEdit.supplier_relation
    delete dataToSendEdit.detail.supplier_relation
    delete dataToSendEdit.segments

    setLoadingButton(true)
    try {
      let response
      if (recordProduct) {
        response = await itemServices.update_item_and_detail(dataToSendEdit.id, dataToSendEdit)
      } else {
        response = await reservationServices.createItemManual(dataToSend)
        if (filesCardRef?.current) {
          filesCardRef.current.uploadPendingFiles(response.hash_)
        }
      }

      setLoadingButton(false)
      onSubmit && onSubmit(response, cardId)

      if (recordProduct) {
        notify(translate('ra.message.success_edit_item'), { type: 'success' })
      } else {
        notify(translate('ra.message.success_create_item'), { type: 'success' })
      }
    } catch (error) {
      if (!recordProduct) {
        notify(translate('ra.message.error_create_item'), { type: 'error' })
      } else {
        notify(translate('ra.message.error_update_item'), { type: 'error' })
      }
      setLoadingButton(false)
    } finally {
      customAwait([refresh])
      handleClose()
    }
  }

  const onChangeNetPrice = (formData, net_price) => {
    setRecord({
      ...formData,
      net_price,
      cancellation_amount: net_price,
    })
  }

  const calculateMaxDate = (formData) => {
    let maxDateValue = null

    if (retrieve) {
      maxDateValue = dayjs(retrieve.date_in)
    } else if (formData?.segments?.[0]?.date_in) {
      maxDateValue = dayjs(formData.segments[0].date_in)
    }
    return maxDateValue
  }

  const getText = (item, optionsText) => (optionsText ? optionsText.map((option) => item[option]) : item.name)

  const renderOption = (props, option) => (
    <li {...props}>
      <Chip
        variant='outlined'
        label={getText(option, ['long_name'])}
        avatar={option?.symbol && <Avatar>{option?.symbol}</Avatar>}
        style={{ backgroundColor: option?.color, cursor: 'pointer' }}
        size='small'
      />
    </li>
  )

  return (
    <Form id='post_create_form' warnWhenUnsavedChanges onSubmit={postSave} record={record}>
      <Dialog open={openModal} maxWidth='xl' fullWidth={true} className='dialog-product'>
        <DialogTitle>{`${
          recordProduct ? translate('ra.action.edit') : translate('ra.action.add')
        } ${translate('ra.title.flight')}`}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={recordProduct ? 12 : 9}>
              {!recordProduct && (
                <>
                  <CardCommon
                    title={translate('ra.message.retrieve_supplier_data')}
                    style={{ marginBottom: 12 }}
                  >
                    <Grid container spacing={3}>
                      <EditInputsWrap md={6} label={translate('ra.field.pnr')} className='small'>
                        <TextInput source='externalId' label='' fullWidth helperText='' />
                      </EditInputsWrap>
                      <Grid item xs={12} md={6}>
                        <FormDataConsumer>
                          {({ formData }) => (
                            <Button
                              variant='contained'
                              onClick={() => getAvailableTickets(formData)}
                              style={{ marginTop: 28 }}
                              disabled={!formData.externalId || loadingRetrieve}
                            >
                              {translate('ra.message.get_available_tickets')}
                            </Button>
                          )}
                        </FormDataConsumer>
                      </Grid>
                      {isNetviax && (
                        <>
                          <EditInputsWrap
                            md={6}
                            label={translate('ra.field.document_number')}
                            className='small'
                          >
                            <SelectInput
                              source='document_number'
                              options={availableTickets}
                              optionValue='document_number'
                              optionsText={['document_number']}
                              multiple
                              showAllOption
                              hideInclude
                              disabled={availableTickets.length === 0}
                            />
                          </EditInputsWrap>

                          <Grid item xs={12} md={6}>
                            <FormDataConsumer>
                              {({ formData }) => (
                                <Button
                                  variant='contained'
                                  onClick={() => getInfoTickets(formData)}
                                  style={{ marginTop: 28 }}
                                  disabled={
                                    !formData.document_number ||
                                    formData.document_number.length === 0 ||
                                    availableTickets.length === 0 ||
                                    loadingRetrieve
                                  }
                                >
                                  {translate('ra.message.retrieve_supplier_data')}
                                </Button>
                              )}
                            </FormDataConsumer>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </CardCommon>
                  <CardCommon title={translate('ra.title.passenger')} style={{ marginBottom: 12 }}>
                    <FormDataConsumer>
                      {({ formData }) => (
                        <Grid container spacing={3}>
                          {getPassengersToSend(formData, false, false, true)?.map((passenger, z) => (
                            <Grid item xs={12} key={z} className='passenger-container'>
                              {PASSENGER_FIELDS.map((field, j) => (
                                <EditInputsWrap
                                  label={translate(`ra.field.${field}`)}
                                  md={j > 1 ? 2 : 3}
                                  key={j}
                                >
                                  <TextField
                                    label=''
                                    size='small'
                                    fullWidth
                                    disabled={j !== 4}
                                    value={getPassengerInfo(passenger.id, field)}
                                    onChange={(e) => onChangePassengerField(e.target.value, z, field)}
                                  />
                                </EditInputsWrap>
                              ))}
                            </Grid>
                          ))}
                          {aditionalPassengers.map((passenger, z) => (
                            <Grid item xs={12} key={z} className='passenger-container'>
                              {passenger.isNew
                                ? NEW_PASSENGER_FIELDS.map((field, j) => (
                                    <EditInputsWrap
                                      label={translate(`ra.field.${field}`)}
                                      md={j === 2 ? 2 : 3}
                                      key={j}
                                    >
                                      <TextField
                                        label=''
                                        size='small'
                                        fullWidth
                                        onChange={(e) => onChangePassenger(e.target.value, z, field)}
                                      />
                                    </EditInputsWrap>
                                  ))
                                : PASSENGER_FIELDS.map((field, j) => (
                                    <EditInputsWrap
                                      label={translate(`ra.field.${field}`)}
                                      md={j > 1 ? 2 : 3}
                                      key={j}
                                    >
                                      {field === 'name' ? (
                                        <Select
                                          fullWidth
                                          size='small'
                                          value={passenger.id}
                                          onChange={(e) => onChangeAditionalPassenger(e.target.value, z)}
                                        >
                                          {getAvailablePassengers(formData, passenger.id).map(
                                            (passenger, i) => (
                                              <MenuItem key={i} value={passenger.id}>
                                                {getPassengerInfo(passenger.id, field)}
                                              </MenuItem>
                                            )
                                          )}
                                        </Select>
                                      ) : (
                                        <TextField
                                          label=''
                                          size='small'
                                          fullWidth
                                          disabled={j !== 4}
                                          value={getPassengerInfo(passenger.id, field)}
                                          onChange={(e) => onChangePassenger(e.target.value, z, field)}
                                        />
                                      )}
                                    </EditInputsWrap>
                                  ))}
                              <Grid
                                item
                                xs={passenger.isNew ? 4 : 1}
                                style={{ paddingTop: 25, textAlign: 'end' }}
                              >
                                <IconButton onClick={() => removePassenger(z)} color='error'>
                                  <Delete />
                                </IconButton>
                              </Grid>
                            </Grid>
                          ))}
                          <Grid item xs={12}>
                            <Button
                              variant='standard'
                              startIcon={<Add />}
                              style={{ color: blue }}
                              onClick={() => addPassenger(true)}
                            >
                              Crear un pasajero
                            </Button>
                            <Button
                              variant='standard'
                              startIcon={<Add />}
                              style={{ color: blue }}
                              onClick={() => addPassenger()}
                            >
                              Agregar otro pasajero
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </FormDataConsumer>
                  </CardCommon>
                </>
              )}
              <CardCommon title={translate('ra.title.reserve_information')} style={{ marginBottom: 12 }}>
                <Grid container spacing={3}>
                  <EditInputsWrap label={translate('ra.field.number')}>
                    <TextInput source='number' label='' fullWidth disabled />
                  </EditInputsWrap>
                  <EditInputsWrap label={translate('ra.field.client')}>
                    <TextInput source='client' label='' fullWidth disabled />
                  </EditInputsWrap>
                  <EditInputsWrap label={translate('ra.field.amount')}>
                    <TextInput source='net_price' label='' fullWidth disabled />
                  </EditInputsWrap>
                  <EditInputsWrap label={translate('ra.field.status')} required>
                    <SelectInput
                      source='status'
                      optionsText={['name']}
                      optionValue='key'
                      options={[
                        { key: CONFIRMED, name: 'CONFIRMADO' },
                        { key: CANCELLED, name: 'CANCELADO OFFLINE' },
                        { key: INVOICED, name: 'EMITIDA' },
                      ]}
                      label=''
                      fullWidth
                      validate={[required()]}
                      disabled={Boolean(retrieve)}
                      defaultValue={INVOICED}
                    />
                  </EditInputsWrap>
                  <EditInputsWrap label={translate('ra.field.source')} required>
                    {loadingSources ? (
                      <Skeleton variant='rectangular' height={40} />
                    ) : (
                      <AutocompleteInput
                        source='source'
                        options={sources}
                        validate={[required()]}
                        optionsText={['long_name']}
                        disableClearable
                        disabled={Boolean(retrieve)}
                        defaultValue={sources.find((elem) => elem.long_name === 'OFFLINE')}
                      />
                    )}
                  </EditInputsWrap>
                  {!recordProduct && (
                    <EditInputsWrap label={translate('ra.field.passenger')} required>
                      <SelectInput
                        source='pax_titular'
                        optionsText={['name', 'surname']}
                        optionValue='id'
                        options={[
                          ...passengersAux,
                          ...aditionalPassengers.filter((elem) => elem.isNew && elem.name && elem.surname),
                        ]}
                        label=''
                        fullWidth
                        validate={[required()]}
                        separator=' '
                      />
                    </EditInputsWrap>
                  )}

                  <Typography
                    variant='h6'
                    component='div'
                    color={blue}
                    style={{ marginLeft: 12, marginBottom: 10, width: '100%' }}
                  >
                    {translate('ra.title.provider_info')}
                  </Typography>
                  <EditInputsWrap label={translate('ra.field.provider')} required>
                    {retrieve ? (
                      <TextInput source='airline' label='' fullWidth disabled />
                    ) : (
                      <>
                        {loadingSupplier ? (
                          <Skeleton variant='rectangular' height={40} />
                        ) : (
                          <SelectInput
                            source='supplier_id'
                            optionsText={['name']}
                            optionValue='id'
                            options={providers}
                            label=''
                            fullWidth
                            validate={[required()]}
                            limitOptions={false}
                            showSearch
                          />
                        )}
                      </>
                    )}
                  </EditInputsWrap>
                  <EditInputsWrap label={`${translate('ra.field.locator')} 1`} required>
                    <TextInput source='pnr_code' label='' fullWidth validate={[required()]} />
                  </EditInputsWrap>
                  <EditInputsWrap label={`${translate('ra.field.locator')} 2`}>
                    <TextInput source='provider_code' label='' fullWidth />
                  </EditInputsWrap>
                  <EditInputsWrap label={translate('ra.field.currency')} required>
                    {loadingCurrencies ? (
                      <Skeleton variant='rectangular' height={40} />
                    ) : (
                      <SelectInput
                        source='currency_id'
                        optionsText={['long_name']}
                        optionValue='id'
                        options={currencies}
                        label=''
                        fullWidth
                        validate={[required()]}
                      />
                    )}
                  </EditInputsWrap>
                  {!recordProduct && (
                    <>
                      {!Boolean(retrieve) && (
                        <FormDataConsumer>
                          {({ formData }) => (
                            <EditInputsWrap label={translate('ra.field.net_provider')} required>
                              <NumberInput
                                source='net_price'
                                label=''
                                fullWidth
                                validate={[required()]}
                                onChange={(e) => onChangeNetPrice(formData, e.target.value)}
                              />
                            </EditInputsWrap>
                          )}
                        </FormDataConsumer>
                      )}
                      <EditInputsWrap label={translate('ra.field.final_sale')}>
                        <NumberInput source='final_sale' label='' fullWidth />
                      </EditInputsWrap>
                      <Typography
                        variant='h6'
                        component='div'
                        color={blue}
                        style={{ marginLeft: 12, marginBottom: 10, width: '100%' }}
                      >
                        {translate('ra.title.tags')}
                      </Typography>
                      <EditInputsWrap label={translate('ra.field.add_tags')} md={12} className='small'>
                        {loadingTags ? (
                          <Skeleton variant='rectangular' height={40} />
                        ) : (
                          <AutocompleteInput
                            source='tag_relation'
                            multiple
                            options={tags}
                            optionsText={['long_name']}
                            renderOption={renderOption}
                            renderTags
                          />
                        )}
                      </EditInputsWrap>
                    </>
                  )}
                </Grid>
              </CardCommon>
              {(!retrieve || !recordProduct) && (
                <CardCommon title={translate('ra.title.aerial_information')} style={{ marginBottom: 12 }}>
                  <Grid container spacing={3}>
                    {!retrieve ? (
                      <>
                        <EditInputsWrap label={translate('ra.field.gds')} required>
                          <TextInput source='gds' label='' fullWidth validate={[required()]} />
                        </EditInputsWrap>

                        <EditInputsWrap label={translate('ra.field.destination_type')} required md={8}>
                          <RadioButtonGroupInput
                            source='destination_type'
                            choices={[
                              { id: 2, name: translate('ra.destination_type.national') },
                              { id: 1, name: translate('ra.destination_type.international') },
                            ]}
                            validate={[required()]}
                          />
                        </EditInputsWrap>
                        <FormDataConsumer>
                          {({ formData }) => (
                            <>
                              {[...Array(formData?.segments?.length).keys()].map((i, index) => (
                                <Grid className='item-container' key={index}>
                                  <EditInputsWrap label={translate('ra.field.flight_number')}>
                                    <TextInput
                                      source={`segments[${i}].scales[0].flightNumber`}
                                      label=''
                                      fullWidth
                                    />
                                  </EditInputsWrap>
                                  <EditInputsWrap label={translate('ra.field.airline')} required>
                                    <TextInput
                                      source={`segments[${i}].airline`}
                                      label=''
                                      fullWidth
                                      validate={[required()]}
                                      inputProps={{ maxLength: 2 }}
                                    />
                                  </EditInputsWrap>
                                  <Hidden mdDown>
                                    <Grid item md={4} />
                                  </Hidden>
                                  <EditInputsWrap label={translate('ra.field.origin')} required>
                                    <AutocompleteInput
                                      source={`segments[${i}].origin`}
                                      options={destinations[i]?.[0] || []}
                                      loading={loadingDestinations[i]?.[0] || false}
                                      optionValue='label'
                                      optionsText={['label']}
                                      onSearch={(value) => getDestinations(value, i, 0)}
                                      resetOnLoad={false}
                                      startAdornment={<Place />}
                                      onChange={(e) => onChangeOrigin(formData, e.target.value, i)}
                                      defaultValue={formData.segments?.[i]?.origin || ''}
                                    />
                                  </EditInputsWrap>
                                  <EditInputsWrap label={translate('ra.field.origin_detail')}>
                                    <SelectInput
                                      source={`segments[${i}].type_origin`}
                                      optionsText={['name']}
                                      optionValue='name'
                                      options={[{ name: 'AEROPUERTO' }]}
                                      label=''
                                      fullWidth
                                      onChange={(e) => onChangeOriginType(formData, e.target.value, i)}
                                      defaultValue={formData.segments?.[i]?.type_origin || 'AEROPUERTO'}
                                    />
                                  </EditInputsWrap>
                                  <EditInputsWrap label={translate('ra.field.datetime_origin')} required>
                                    <DateTimePickers
                                      source={`segments[${i}].date_in`}
                                      validate={[required()]}
                                      minDateTime={
                                        i !== 0 && formData?.segments?.[i - 1]?.date_out
                                          ? dayjs(formData.segments[i - 1].date_out)
                                          : null
                                      }
                                    />
                                  </EditInputsWrap>
                                  <EditInputsWrap label={translate('ra.field.destination')} required>
                                    <AutocompleteInput
                                      source={`segments[${i}].destination`}
                                      options={destinations[i]?.[1] || []}
                                      loading={loadingDestinations[i]?.[1] || false}
                                      optionValue='label'
                                      optionsText={['label']}
                                      onSearch={(value) => getDestinations(value, i, 1)}
                                      resetOnLoad={false}
                                      startAdornment={<Place />}
                                      onChange={(e) => onChangeDestination(formData, e.target.value, i)}
                                      defaultValue={formData.segments?.[i]?.destination || ''}
                                    />
                                  </EditInputsWrap>
                                  <EditInputsWrap label={translate('ra.field.destination_detail')}>
                                    <SelectInput
                                      source={`segments[${i}].type_destination`}
                                      optionsText={['name']}
                                      optionValue='name'
                                      options={[{ name: 'AEROPUERTO' }]}
                                      label=''
                                      fullWidth
                                      onChange={(e) => onChangeDestinationType(formData, e.target.value, i)}
                                      defaultValue={formData.segments?.[i]?.type_destination || 'AEROPUERTO'}
                                    />
                                  </EditInputsWrap>
                                  <EditInputsWrap label={translate('ra.field.datetime_destination')} required>
                                    <DateTimePickers
                                      source={`segments[${i}].date_out`}
                                      validate={[required()]}
                                      minDateTime={
                                        formData.segments?.[i]?.date_in
                                          ? dayjs(formData.segments?.[i]?.date_in)
                                          : i > 0 &&
                                            (formData.segments?.[i - 1]?.date_out ||
                                              formData.segments?.[i - 1]?.date_in)
                                          ? dayjs(
                                              formData.segments?.[i - 1]?.date_out ||
                                                formData.segments?.[i - 1]?.date_in
                                            )
                                          : null
                                      }
                                    />
                                  </EditInputsWrap>
                                  <Grid item xs={12} className='delete-item'>
                                    <Button
                                      variant='outlined'
                                      startIcon={<Delete />}
                                      color='error'
                                      onClick={() => removeSegment(formData, i)}
                                      disabled={formData?.segments?.length === 1}
                                    >
                                      {translate('ra.title.delete_segment')}
                                    </Button>
                                  </Grid>
                                </Grid>
                              ))}
                              <Grid item xs={12}>
                                <Button
                                  variant='standard'
                                  startIcon={<Add />}
                                  style={{ marginBottom: 25, color: blue }}
                                  onClick={() => addSegment(formData)}
                                >
                                  {translate('ra.title.add_segment')}
                                </Button>
                              </Grid>
                            </>
                          )}
                        </FormDataConsumer>
                      </>
                    ) : (
                      <>
                        <Grid item xs={12}>
                          <Button
                            variant='contained'
                            onClick={onClickManuallyMode}
                            style={{ marginBottom: 12 }}
                            disabled={loadingRetrieve}
                          >
                            {translate('ra.message.enter_data_manually')}
                          </Button>
                        </Grid>
                        <Grid item xs={12}>
                          <AirScaleRow air={retrieve} />
                        </Grid>
                      </>
                    )}

                    <FormDataConsumer>
                      {({ formData }) => (
                        <>
                          {!recordProduct && (
                            <>
                              <EditInputsWrap label={translate('ra.field.titular_passenger')}>
                                <SelectInput
                                  source='pax_info'
                                  optionsText={['name', 'surname']}
                                  optionValue='id'
                                  options={[
                                    ...passengersAux,
                                    ...aditionalPassengers.filter(
                                      (elem) => elem.isNew && elem.name && elem.surname
                                    ),
                                  ]}
                                  label=''
                                  fullWidth
                                  separator=' '
                                  defaultValue={
                                    passengers?.find((elem) => elem.is_titular)?.id ||
                                    passengers?.[0]?.id ||
                                    ''
                                  }
                                  onChange={(e) => onChangeTitularPassenger(e.target.value, formData)}
                                />
                              </EditInputsWrap>
                              <Grid item xs={8} />
                            </>
                          )}
                          {formData.pax_info &&
                            PASSENGER_FIELDS.map((field, j) => (
                              <EditInputsWrap
                                label={translate(`ra.field.${field}`)}
                                md={j < 2 ? 3 : 2}
                                key={j}
                              >
                                <TextField
                                  label=''
                                  size='small'
                                  fullWidth
                                  disabled
                                  value={getPassengerInfo(formData.pax_info, field)}
                                />
                              </EditInputsWrap>
                            ))}

                          <Typography
                            variant='h6'
                            component='div'
                            color={blue}
                            style={{ marginLeft: 12, marginBottom: 10, width: '100%' }}
                          >
                            {translate('ra.title.cancellation_cost')}
                          </Typography>
                          <Typography
                            variant='h6'
                            component='div'
                            style={{ marginLeft: 12, marginBottom: 10, width: '100%', fontSize: '1em' }}
                          >
                            {translate('ra.title.cancellation_cost_provider')}
                          </Typography>
                          <EditInputsWrap label={translate('ra.field.date')} required>
                            <DatePickers
                              source='cancellation_date'
                              required
                              maxDate={calculateMaxDate(formData)}
                              viewsShow={false}
                              defaultValue={dayjs()}
                            />
                          </EditInputsWrap>
                          <EditInputsWrap label={translate('ra.field.amount')} required>
                            <NumberInput
                              source='cancellation_amount'
                              label=''
                              fullWidth
                              validate={[required()]}
                            />
                          </EditInputsWrap>
                        </>
                      )}
                    </FormDataConsumer>
                  </Grid>
                </CardCommon>
              )}
              <HasPermission resource='file-upload' permission={PERMISSION_GET}>
                <CardCommon title={translate('ra.title.files')}>
                  <FilesCard ref={filesCardRef} recordProduct={recordProduct} />
                </CardCommon>
              </HasPermission>
            </Grid>
            {!recordProduct && (
              <Grid item xs={3}>
                <Grid position='sticky' top={0}>
                  <CardCommon title={translate('ra.title.contact')} className='small-fields'>
                    <Grid container spacing={3}>
                      <EditInputsWrap label={translate('ra.field.name')} md={12}>
                        <TextInput source='contact.name' label='' fullWidth />
                      </EditInputsWrap>
                      <EditInputsWrap label={translate('ra.field.surname')} md={12}>
                        <TextInput source='contact.surname' label='' fullWidth />
                      </EditInputsWrap>
                      <EditInputsWrap label={translate('ra.field.email')} md={12}>
                        <TextInput source='contact.email' label='' fullWidth />
                      </EditInputsWrap>
                      <EditInputsWrap label={translate('ra.field.phone_number')} md={12}>
                        <TextInput source='contact.phone_number' label='' fullWidth />
                      </EditInputsWrap>
                    </Grid>
                  </CardCommon>
                </Grid>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions className='dialog-actions'>
          <Grid>
            <FormDataConsumer>
              {({ formData }) => {
                let isDisabled = !workUnit || !product || !formData.source || !formData.currency_id

                if (!retrieve) {
                  isDisabled =
                    isDisabled ||
                    !formData.supplier_id ||
                    !(formData.net_price !== null && formData.net_price !== undefined) ||
                    !formData.destination_type ||
                    !formData.segments?.[0]?.origin ||
                    !formData.segments?.[0]?.date_in ||
                    !formData.segments?.[0]?.airline
                }

                return (
                  <>
                    <LoadingButton
                      loading={loadingButton}
                      variant='contained'
                      startIcon={<Save />}
                      form='post_create_form'
                      type='submit'
                      disabled={!recordProduct && isDisabled}
                    >
                      {translate('ra.action.save')}
                    </LoadingButton>
                  </>
                )
              }}
            </FormDataConsumer>
          </Grid>
          <Button onClick={handleClose} disabled={loadingButton} variant='outlined' color='error'>
            {translate('ra.action.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Form>
  )
}
